/**
 * A beyond-cdn-events/1 channel: one sequence domain, named `org:<id>`, `app:<id>` or `job:<id>`.
 *
 * A name grants nothing. The service authorizes every join against the resource the name points to.
 */
export class Channel {
	static #prefixes = { organization: 'org', application: 'app', job: 'job' };
	static #pattern = /^(org|app|job):([A-Za-z0-9][A-Za-z0-9_.-]{0,127})$/;

	#level;
	get level() {
		return this.#level;
	}

	#resource;
	/**
	 * The identifier of the organization, application or job
	 */
	get resource() {
		return this.#resource;
	}

	get name() {
		return `${Channel.#prefixes[this.#level]}:${this.#resource}`;
	}

	/**
	 * @param {'organization' | 'application' | 'job'} level
	 * @param {string} resource
	 */
	constructor(level, resource) {
		if (!Channel.#prefixes[level]) throw new RangeError(`Unknown channel level "${level}"`);
		if (!Channel.#pattern.test(`${Channel.#prefixes[level]}:${resource}`)) throw new RangeError(`Invalid channel resource "${resource}"`);
		this.#level = level;
		this.#resource = resource;
	}

	/**
	 * @param {string} name
	 * @returns {Channel | undefined} undefined when the name does not follow the naming rules
	 */
	static parse(name) {
		const match = Channel.#pattern.exec(String(name));
		if (!match) return;
		const level = Object.keys(Channel.#prefixes).find(key => Channel.#prefixes[key] === match[1]);
		return new Channel(level, match[2]);
	}

	/**
	 * The channel an event belongs to: its job when the stream names one, otherwise its application,
	 * otherwise its organization.
	 *
	 * @param {{organization: string, application?: string, job?: string}} stream
	 */
	static of(stream) {
		if (stream.job) return new Channel('job', stream.job);
		if (stream.application) return new Channel('application', stream.application);
		return new Channel('organization', stream.organization);
	}

	toString() {
		return this.name;
	}
}
